import { formatAppDate } from '../utility/dateLocale';
import { useMemo, useState } from 'react';
import {
  Group,
  Text,
  Card,
  Table,
} from '@mantine/core';
import { useTranslation } from 'react-i18next';
import {
  DndContext,
  DragOverlay,
  DragEndEvent,
  DragStartEvent,
  useDroppable
} from '@dnd-kit/core';
import { FileText} from 'tabler-icons-react';
import '../pages/KanbanColumn.css';
import {Stage, Opportunity, OpportunityForm, STAGES, stageColors, stageIcons, TABLE_MAX_HEIGHT } from './OpportunityPageTypes';
import { calculateStageDuration, handleStageChange } from './OpportunityPageUtilities';
import { OpportunityCard, DraggableOpportunityCard } from './OpportunityPageCards';
import { getStageLabel } from '../i18n/stageLabels';
import { usePageScrollPanelStyle } from '../utility/mobilePageLayout';

  /* ───────────────────────────────
     VISÃO ROAD MAP
  ──────────────────────────────── */
  
export function OpportunityRoadMapView({
    opportunities,
    updateOpportunity,
    onClick
  }: {
    opportunities: Opportunity[];
    updateOpportunity: (opp: OpportunityForm, id: string) => void;
    onClick: (opp: Opportunity) => void;
  }) {
    const { t, i18n } = useTranslation();
    const scrollPanelStyle = usePageScrollPanelStyle(TABLE_MAX_HEIGHT);
    const [activeDraggable, setActiveDraggable] = useState<{
      opportunity: Opportunity;
      record: Stage;
    } | null>(null);
  
    const stageLabels = useMemo(
      () => Object.fromEntries(STAGES.map((stage) => [stage, getStageLabel(stage, t)])),
      [t, i18n.language],
    );

    const handleDragStart = (event: DragStartEvent) => {
      const { active } = event;
      opportunities.forEach((opp) => {
        opp.stages.forEach((stage) => {
          if (`${opp.id}/${stage.id}` === active.id) {
            setActiveDraggable({ opportunity: opp, record: stage });
          }
        });
      });
    };
  
    const handleDragEnd = (event: DragEndEvent) => {
      const { over } = event;
      if (activeDraggable && over) {
        const [, targetStage] = (over.id as string).split('/');

        console.log("OVER", over)
        if (targetStage === 'Closed'){
          console.log("ENTROU NO FECHAMENTO")
          console.log("ACTIVE DRAGGABLE", activeDraggable)
          if (!activeDraggable.opportunity.noSalesMade && !activeDraggable.opportunity.products.some(p => p.sold === true)){
            alert(t('opportunity.alerts.cannotCloseWithoutSales'));
            setActiveDraggable(null);
            return;
          } 
        }

        if (activeDraggable.record.name === 'Closed'){
            alert(t('opportunity.alerts.alreadyClosing'));
            setActiveDraggable(null);
            return;
        }
        if (targetStage !== activeDraggable.record.name) {
          handleStageChange(activeDraggable.opportunity, targetStage as any, updateOpportunity);
        }
      }
      setActiveDraggable(null);
    };
  
    return (
      <DndContext onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
        <div style={scrollPanelStyle}>
          <Table highlightOnHover withBorder style={{ width: '100%', tableLayout: 'auto' }}>
            <thead>
              <tr>
                {STAGES.map((stage) => (
                  <th
                    key={stage}
                    style={{
                      width: '200px',
                      textAlign: 'center',
                      position: 'sticky',
                      top: 0,
                      backgroundColor: '#fff',
                      zIndex: 1,
                      borderBottom: '1px solid #ccc'
                    }}
                  >
                    <Text>{stageLabels[stage]}</Text>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {opportunities.map((opp) => (
                <OpportunityRow key={opp.id} opportunity={opp} onClick={onClick} />
              ))}
            </tbody>
          </Table>
        </div>
  
        <DragOverlay>
          {activeDraggable ? (
            <OpportunityCard
              opportunity={activeDraggable.opportunity}
              record={activeDraggable.record}
              color={stageColors[activeDraggable.record.name] || '#000'}
              icon={stageIcons[activeDraggable.record.name] || <FileText size={18} />}
            />
          ) : null}
        </DragOverlay>
      </DndContext>
    );
  }
  
  export  interface OpportunityRowProps {
    opportunity: Opportunity;
    onClick: (opp: Opportunity) => void;
  }
  
  export  function OpportunityRow({ opportunity, onClick }: OpportunityRowProps) {
    const currentStage = opportunity.stages.filter(st => st.finished_at === null)[0] || opportunity.stages[0];
  
    return (
      <tr>
        {STAGES.map((stage) => (
          <StageCell
            key={stage}
            opportunity={opportunity}
            stageName={stage}
            currentStage={currentStage}
            onClick={() => onClick(opportunity)}
          />
        ))}
      </tr>
    );
  }
  
  export  interface StageCellProps {
    opportunity: Opportunity;
    stageName: string;
    currentStage: Stage;
    onClick: (opp: Opportunity) => void;
  }
  
  export  function StageCell({ opportunity, stageName, currentStage, onClick }: StageCellProps) {
    const { t } = useTranslation();
    const stageLabel = getStageLabel(stageName, t);
    const droppableId = `${opportunity.id}/${stageName}`;
    const { setNodeRef, isOver } = useDroppable({ id: droppableId });
    const stageRecords = opportunity.stages.filter((st) => st.name === stageName);
    const isCurrent = currentStage.name === stageName && !currentStage.finished_at;
  
    return (
      <td
        ref={setNodeRef}
        style={{
          minWidth: '200px',
          textAlign: 'center',
          position: 'relative'
        }}
      >
        {isOver && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              border: `2px dashed ${stageColors[stageName]}`,
              background: 'rgba(255,255,255,0.8)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 9999,
              pointerEvents: 'none'
            }}
          >
            <Text size="sm" weight={700} style={{ color: stageColors[stageName] }}>
              {stageLabel}
            </Text>
          </div>
        )}
  
        {stageRecords.map((record, idx) => {
          const color = stageColors[stageName] || '#000';
          const icon = stageIcons[stageName] || <FileText size={18} />;
          const isLastRecord = idx === stageRecords.length - 1;
          const stageOrder = opportunity.stages
                                        .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()) // Ordena por data de criação em ordem crescente
                                        .findIndex((st) => st.id === record.id) + 1;
          if (isCurrent && isLastRecord) {
            return (
              <DraggableOpportunityCard
                key={record.id}
                opportunity={opportunity}
                onClick={() => onClick(opportunity)}
              />
            );
          }
          return (
            <Card
              key={record.id}
              shadow="sm"
              radius="sm"
              p="xs"
              style={{
                width: 220,
                minHeight: 100,
                margin: 'auto',
                borderLeft: `6px solid ${color}`,
                backgroundColor: '#fff',
                textAlign: 'left'
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: 4
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <div style={{ marginRight: 6, display: 'flex', alignItems: 'center', color }}>
                    {icon}
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      overflow: 'hidden',
                      textAlign: 'left',
                      minWidth: 0
                    }}
                  >
                    <Text
                      weight={700}
                      size="xs"
                      style={{
                        fontSize: '10px',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}
                    >
                      {opportunity.id.length > 15
                      ? 'ID: ' + opportunity.id.slice(0, 15) + '...'
                      : 'ID: ' + opportunity.id}
                    </Text>
                    <Text
                      weight={600}
                      size="xs"
                      style={{
                        width: '130px',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}
                    >
                      {opportunity.name}
                    </Text>
                  </div>
                </div>
                <div
                  style={{
                    backgroundColor: color,
                    borderRadius: '50%',
                    width: 20,
                    height: 20,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <Text
                    weight={700}
                    size="xs"
                    style={{ color: '#fff', fontSize: '10px', lineHeight: 1 }}
                  >
                    {stageOrder}
                  </Text>
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <Group position="apart" spacing="xs">
                  <Text size="xs" color="dimmed">
                    {t('opportunity.roadmap.start')}
                  </Text>
                  <Text size="xs">{formatAppDate(record.created_at)}</Text>
                </Group>
                {record.finished_at && (
                  <Group position="apart" spacing="xs">
                    <Text size="xs" color="dimmed">
                      {t('opportunity.roadmap.finish')}
                    </Text>
                    <Text size="xs">{formatAppDate(record.finished_at)}</Text>
                  </Group>
                )}
                <Group position="apart" spacing="xs">
                  <Text size="xs" color="dimmed">
                    {t('opportunity.roadmap.timeSpent')}
                  </Text>
                  <Text size="xs">{calculateStageDuration(record)}d</Text>
                </Group>
              </div>
            </Card>
          );
        })}
      </td>
    );
  }
  